//#region IMPORT
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Commons from '../../../../Services/Common/Common';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { setSearchInfo } from '../../../../Reducer/SearchReducer';
import useDelete from '../../../../hooks/useDeleteN';
import { useSave } from '../../../../hooks/useSaveN';
import useCompanyCustDep from '../../../../hooks/useCompanyCustDep';
import { MapCompanyRowsToCoList } from '../../../../hooks/adminCompanyMaster';
import { COU_BASIC } from './index';
import {
  useDelete1,
  useSave1,
  useSearch1,
  useSearch2,
} from './COU_BASIC_QUERY';
import { COU_BASIC_Schema } from './COU_BASIC_Schema';
//#endregion

const SCREEN_MENU_ID = 'CompanyMenu';

const COU_BASIC_DUPLICATE_KEY_DEFS = [
  { field: 'CO_CD', name: '회사' },
  { field: 'PL_TYPE', name: '구분' },
];

const PickFieldCouBasicDuplicate = (item, field) => {
  if (!item || field == null) return undefined;
  if (field === 'CO_CD') return item.CO_CD ?? item.coCd;
  if (field === 'PL_TYPE') return item.PL_TYPE ?? item.plType;
  return item[field];
};

const BuildCouBasicDuplicateKey = (item) =>
  JSON.stringify(
    COU_BASIC_DUPLICATE_KEY_DEFS.map((k) =>
      (PickFieldCouBasicDuplicate(item, k.field)?.toString().trim() ?? '')
    )
  );

const CheckDuplicateLogic1 = (dispatch, rows, changedRows) => {
  const rowKeys = new Set(rows.map(BuildCouBasicDuplicateKey).filter(Boolean));
  const newRows = changedRows.filter((f) => f.newYn === 'Y');
  const changedKeys = newRows.map(BuildCouBasicDuplicateKey).filter(Boolean);
  const duplicates = changedKeys.filter((k) => rowKeys.has(k));

  if (duplicates.length === 0) return true;

  const firstKey = duplicates[0];
  const exampleRow = newRows.find(
    (f) => BuildCouBasicDuplicateKey(f) === firstKey
  );
  const exampleDisplay = COU_BASIC_DUPLICATE_KEY_DEFS.map((k) => {
    const v =
      PickFieldCouBasicDuplicate(exampleRow, k.field)?.toString().trim() ?? '';
    return `${k.name}: '${v}'`;
  }).join(', ');

  dispatch(
    showSnackbar({
      message: `중복된 데이터가 있습니다: ${duplicates.length}건 (${exampleDisplay})`,
      type: 'warning',
    })
  );
  return false;
};

function COU_BASICF() {
  const dispatch = useDispatch();
  const { userInfo, lanInfo, companyInfo } = useSelector(
    (state) => state.BaseInfo
  );
  const searchInfo = useSelector((state) => state.SearchInfo);
  const isAdmin = userInfo?.authCd === 'ADMIN';

  const { handleCoChange, validateSelection } = useCompanyCustDep({
    requireCompany: true,
  });

  const gridRef1 = useRef();
  const selectedRowNosRef1 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);
  const currentRowIndexRef1 = useRef(null);

  const [rowData1, setRowData1] = useState([]);
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [deletedRowData1, setDeletedRowData1] = useState([]);
  const [plTypeList, setPlTypeList] = useState([]);

  const notDataLabel =
    lanInfo.length === 0
      ? ''
      : lanInfo.find((f) => f.LAN_TYPE !== 'MENU' && f.LAN_ID === 'NotData')
          ?.LAN_NM ?? '';

  const ApplyAdminCoListLogic = useCallback(() => {
    const list = MapCompanyRowsToCoList(companyInfo);
    const coList = [{ value: '전체', name: '전체' }, ...list];
    const prevCoCd = String(searchInfo?.coCd ?? '').trim();
    const defaultCoCd =
      prevCoCd && coList.some((c) => c.value === prevCoCd)
        ? prevCoCd
        : '전체';

    dispatch(
      setSearchInfo({
        coList,
        coCd: defaultCoCd,
      })
    );
  }, [companyInfo, dispatch, searchInfo?.coCd]);

  useEffect(() => {
    if (!isAdmin || !companyInfo?.length) return;
    ApplyAdminCoListLogic();
  }, [isAdmin, companyInfo, ApplyAdminCoListLogic]);

  useSearch2({
    onEmpty: () => {
      if (notDataLabel) {
        dispatch(
          showSnackbar({
            message: notDataLabel,
            type: 'info',
          })
        );
      }
    },
    onApiError: (errRow) => {
      dispatch(
        showSnackbar({
          message: errRow?.message ?? '조회 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    },
    onSuccess: (list) => {
      setPlTypeList(list ?? []);
    },
  });

  const { refetch: SearchLogic1 } = useSearch1({
    coCd: searchInfo?.coCd,
    onErrorRow: (errRow) => {
      dispatch(
        showSnackbar({
          message: errRow?.message ?? '조회 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    },
    onSuccess: (rows) => {
      const list = rows || [];
      if (!list.length) {
        setRowData1([]);
        return;
      }
      setRowData1(list);
    },
  });

  const saveMutation = useSave1({ userInfo });
  const deleteMutation = useDelete1();

  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    deleteMutation,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    deletedRowData: deletedRowData1,
    rowData: rowData1,
    setChangedRowData: setChangedRowData1,
  });

  const saveCouBasic = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '회사별 메뉴',
    schema: { fields: COU_BASIC_Schema.fields },
    getChangedRows: () => changedRowData1.filter((r) => !r.deleteYn),
    getDeletedRows: () => deletedRowData1,
    requestDelete: deleteHandler1.DeleteLogic,
    requestSave: saveMutation.runAsync,
    onSuccessRefresh: async ({ saveDetailData, deleteDetailData }) => {
      await SearchLogic1();
      const saveFail = (saveDetailData ?? []).some(
        (r) => r?.isSuccess === false
      );
      const delFail = (deleteDetailData ?? []).some(
        (r) => r?.isSuccess === false
      );
      if (saveFail || delFail) return;
      setChangedRowData1([]);
      setDeletedRowData1([]);
      setSelectedRowData1([]);
      gridRef1?.current?.api?.deselectAll?.();
    },
  });

  const RefreshLogic1 = async () => {
    setChangedRowData1([]);
    setDeletedRowData1([]);
    setSelectedRowData1([]);
    gridRef1?.current?.api?.deselectAll?.();
    await SearchLogic1();
  };

  const HandleCoChangeLogic = (raw) => {
    if (isAdmin) {
      const newCoCd = raw?.target?.value !== undefined ? raw.target.value : raw;
      dispatch(setSearchInfo({ coCd: newCoCd }));
      Commons.Storages('set', 'companyInfo', {
        coCd: newCoCd || '',
        custCd: '',
        depCd: '',
      });
      return;
    }
    handleCoChange(raw);
  };

  const SaveLogic1 = async () => {
    const changedRows = changedRowData1.filter((r) => !r.deleteYn);
    if (changedRows.length > 0) {
      const invalidRowsName = changedRows.filter(
        (item) =>
          !item.CO_CD ||
          !String(item.CO_CD).trim() ||
          item.CO_CD === '전체'
      );
      if (invalidRowsName.length > 0) {
        dispatch(
          showSnackbar({
            message: '회사를 선택 하세요.',
            type: 'warning',
          })
        );
        return;
      }
      const deletedNoSet = new Set((deletedRowData1 ?? []).map((r) => r.no));
      const rowDataForDuplicate = rowData1.filter(
        (r) => !deletedNoSet.has(r.no)
      );
      if (
        !CheckDuplicateLogic1(dispatch, rowDataForDuplicate, changedRows)
      ) {
        return;
      }
    }
    await saveCouBasic.run();
  };

  const DeleteClick1 = () => {
    const newRowExists = selectedRowData1.some((row) => row.newYn === 'Y');
    if (newRowExists) {
      dispatch(
        showSnackbar({
          message: '데이터 입력중입니다. 저장 후 시도하세요.',
          type: 'warning',
        })
      );
      return;
    }
    deleteHandler1.MarkedForDeletion(selectedRowData1);
  };

  const handleRowSelection1 = (rowData, rowIndex) => {
    currentRowIndexRef1.current = rowIndex;
    setSelectedRowData1([rowData]);
  };

  return (
    <div className="wrap">
      <COU_BASIC
        dispatch={dispatch}
        showSnackbar={showSnackbar}
        userInfo={userInfo}
        gridRef1={gridRef1}
        coList={searchInfo?.coList ?? []}
        coCd={searchInfo?.coCd ?? ''}
        plTypeList={plTypeList}
        rowData1={rowData1}
        setRowData1={setRowData1}
        selectedRowData1={selectedRowData1}
        setSelectedRowData1={setSelectedRowData1}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        selectedRowNosRef1={selectedRowNosRef1}
        lastCheckedIndexRef1={lastCheckedIndexRef1}
        currentRowIndexRef1={currentRowIndexRef1}
        SearchLogic1={SearchLogic1}
        RefreshLogic1={RefreshLogic1}
        SaveLogic1={SaveLogic1}
        MarkedForDeletion1={deleteHandler1.MarkedForDeletion}
        RestoreRow1={deleteHandler1.RestoreRow}
        DeleteNewRow1={deleteHandler1.DeleteNewRow}
        DeleteClick1={DeleteClick1}
        handleRowSelection1={handleRowSelection1}
        handleCoChange={HandleCoChangeLogic}
        validateSelection={validateSelection}
      />
    </div>
  );
}

export default COU_BASICF;
